"use client";

// FOUNDER CORRECTION 2026-09-25 · item 2 — REAL CARD DETAILS ON THE REVIEW PAGE, IN THE KIT'S OWN PANEL.
//
// The Kit's CARD DETAILS step (sg-beta-v2.01.html, `payCardDisplay`) collects purchaser + traveller on
// `/mountain-<code>/booking/review`. The card used to be taken on a second page (the checkout app). It is
// now taken HERE: Stripe's Payment Element mounts in this top-level document — Stripe supports that and
// forbids nesting it inside another iframe — portalled into the empty `[data-sg-inpage-card]` slot the
// export reserves inside CARD DETAILS (a1-inpage-card-export-patch.mjs). The panel around it copies the
// Kit's own "Payment details" block: same border, radius, 11px caps heading, 10px caps labels, 46px inputs.
//
// WHAT PAY DOES, IN ORDER — every money decision stays where it already lived:
//   1. `createDesignOrder` → `/api/orders/design-cart` — the SAME server-priced draft order the A1 chain
//      created before; the displayed total is only an assertion the backend refuses on mismatch. A reload
//      replays the attempt key and gets an earlier visit's order back;
//   2. that order's signed-webhook status is read FIRST — an order whose card is already authorized (or in
//      flight) is waited on, never re-validated or re-confirmed (the Payment Element is empty after a reload);
//   3. country + postal code are present (F9: address before the intent), then `/api/orders/<id>/payment-intent`
//      — the backend prices the intent from the order, manual capture; the page refuses to confirm unless the
//      intent authorizes exactly the total it showed. An intent Stripe says already holds a card is waited on;
//   4. only for a still-payable intent: the Payment Element validates the card (`elements.submit()`) and
//      `stripe.confirmPayment` hands it to Stripe (3-D Secure opens Stripe's own modal);
//   5. poll `/api/orders/<id>/status` until the signed webhook has written `pending_acceptance` / `paid`
//      (or `payment_failed`). ONLY THEN is the design told anything but a notice, and only then is the
//      checkout attempt key ended — a reload before that replays the key, gets the SAME order back, and
//      `runInpagePayment` waits on it instead of confirming the card again. That same settled read — and only
//      it — is offered to GA4 as `purchase` (`inpagePurchase` → `emitPurchaseOnce`, consent- and live-host-
//      gated, once per order id): founder ruling 2026-09-30 counts a card-authorized pending_acceptance order.
// The design is never told `booked`. An authorized order is waiting on the operator (explicit acceptance
// or the 48h no-objection window) — the done screen says exactly that, from the shared acceptance copy.
//
// M4 · PAY AT THE SHOP (SNOWCLUB-STAGING-M4-PAY-AT-SHOP-FE-20261026). Only when the server's own quote carries the exact M4 zero-due
// plan (`quote.payment`, validated by the BFF and again by `quoteDesignOrder`) does this panel drop the card: Stripe.js is not loaded,
// no Payment Element is mounted, no billing address is asked for or published as a gap, and Pay (`reserve`) creates the SAME
// server-priced order through the same attempt key and stops at the server's reservation status. Nothing is charged; the result rides
// the reason path (never `authorized`, never an `orderId`), the attempt is kept (a press or reload replays the SAME order) and no
// GA4 payment or purchase event is offered. A reload judges a recorded M4 order only as a reservation (`checkReservation`).

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { publicRuntimeValue } from "@/lib/public-runtime-config";
import { loadStripe } from "@stripe/stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { Alert, AlertActions, AlertDescription, AlertDismiss, AlertTitle } from "@repo/ui/components/alert";
import { FieldError, FieldErrorSummary } from "@repo/ui/components/field";
import { COUNTRY_OPTIONS } from "@repo/ui/lib/countries";
import { orderPhase } from "@repo/ui/lib/order-status";
import {
  FIELD_ERROR_COLOR,
  fieldMessageId,
  focusFirstError,
  revealOnBlur,
  revealOnSubmit,
  safeUserMessage,
  settleOnChange,
  visibleErrors,
} from "@repo/ui/lib/form-feedback";
import { readDesignCart } from "@/components/marketplace/DesignCartLines";
import type { CartItem } from "@/lib/cart-store";
import { isSignedIn, useCartStore } from "@/lib/cart-store";
import type { PayOrder } from "@/lib/booking-pay-bridge";
import { sendAnalyticsEvent } from "@/lib/analytics-event";
import { emitPurchaseOnce } from "@/lib/analytics-purchase";
import { endCheckoutAttempt, endCheckoutAttemptIf, peekCheckoutAttempt } from "@/lib/checkout-attempt";
import { accountsUrl } from "@/lib/account-links";
import { checkoutReturnUrl } from "@/lib/checkout-return";
import { createDesignOrder, sessionIdentityOf, startDesignQuoteWatch } from "@/lib/design-pay";
import { loadOwnedSavedCards, savedCardLabel, savedCardsFor, savedChoiceFor } from "@/lib/saved-cards";
import type { OwnedSavedCards, OwnedSavedChoice } from "@/lib/saved-cards";
import { QuoteBlock } from "./QuoteBreakdown";
import { RENTAL_FIELD_LABEL, RentalGuests, rentalFieldId, rentalUnitId } from "./RentalGuests";
import { blankRentalPerson, buildRentalGuests, rentalAssignmentErrors, rentalPersonErrors, usedRentalPeopleErrors } from "@/lib/rental-terms";
import type { RentalAssignments, RentalGuestField, RentalPerson } from "@/lib/rental-terms";
import type { QuoteState } from "./QuoteBreakdown";
import {
  billingAddress,
  cartsAfterSettledOrder,
  findAttemptSnapshot,
  checkPaymentStatus,
  checkReservation,
  markOrderSnapshotSettled,
  RESERVATION_UNCONFIRMED,
  readOrderSnapshot,
  resumeAfterRedirect,
  runInpagePayment,
  saveOrderSnapshot,
  startFreshAfterDecline,
} from "@/lib/inpage-card";
import type { OrderCartSnapshot } from "@/lib/inpage-card";
import type { InpagePaymentState, InpageResult, OrderStatusRead, ReservationFacts } from "@/lib/inpage-card";

/** The slot the export reserves inside CARD DETAILS. */
export const INPAGE_CARD_SLOT = "[data-sg-inpage-card]";

// Read by name so Next inlines it into the client bundle (`process.env` as an object is empty there).
// eslint-disable-next-line no-restricted-properties
const publishableKey = () => publicRuntimeValue("NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY") ?? "";
const NOT_CHARGED = "Nothing was charged.";
// W17 — provider and network failures in plain words; the raw provider text (object ids, codes) never reaches the page.
const START_FAILED = `We couldn't start the card payment — try again in a moment. ${NOT_CHARGED}`;
const OFFLINE = `We couldn't reach the payment service — check your connection and try again. ${NOT_CHARGED}`;
const CARD_NOT_AUTHORIZED = "Your card could not be authorized — try again or use a different card.";
const PLACED = "This order has already been placed — do not pay again. It is in My Trips.";
const STILL_WAITING =
  "We still can't confirm this payment. Do not pay again — check again in a minute; the order will also update in My Trips.";
// M4 · pay at the shop
const NO_CARD = "No card is taken for a pay-at-the-shop reservation.";
const CART_CHANGED_AFTER_CARD = `A card payment was already started on this page for an earlier version of your cart. Reload the page before booking again. Nothing new was charged.`;
type ReasonResult = Extract<InpageResult, { reason: string }>;

/** W17 — the billing controls' stable ids (the error summary moves focus to them) and their wording. */
const FIELD = { country: "sg-pay-billing-country", postal: "sg-pay-billing-postal", card: "sg-pay-card" } as const;
type FieldKey = keyof typeof FIELD;
const FIELD_LABEL: Record<FieldKey, string> = { country: "Billing country", postal: "Postal code", card: "Card details" };

declare global {
  interface Window {
    /** W17 — this panel's current billing errors (messages only), read by the design's one CARD DETAILS summary */
    __SG_PAY_BILLING?: { country: string; postal: string; recover: boolean };
  }
}

const lineCount = () => readDesignCart(window.localStorage).reduce((n, l) => n + (Number(l.qty) || 0), 0);

// CARD ENTRY THAT NEVER APPEARS (production replay 2026-10-20: Stripe's iframe stayed at 2px / opacity 0, `ready`
// never came, and the panel said nothing). Every way the Payment Element can fail to appear — Stripe's `loaderror`,
// Stripe.js not arriving, or no `ready` in time — is first retried once on its own (a fresh Payment Element, and Stripe.js
// again if it never arrived), then said in the panel with a Try again that does the same. Each failure is logged with
// Stripe's code so a replay names the cause. A retry remounts ONLY the card entry: it never creates, confirms or replays
// an order; the billing fields, this visit's order and every do-not-pay-again lock live in the panel, which stays mounted.
const CARD_LOAD_TIMEOUT_MS = 10_000;
const CARD_LOAD_FAILED = `Stripe's secure card form hasn't loaded, so no card can be entered yet. ${NOT_CHARGED} The details you've entered are kept.`;
type CardLoad = { kind: "loading" } | { kind: "ready" } | { kind: "failed"; reason: string };
/** Stripe's error code (or type) for a `loaderror`, kept for diagnosis on the panel — never Stripe's message, which can name objects. */
const loadErrorReason = (error: unknown) => {
  const e = (error ?? {}) as { code?: unknown; type?: unknown };
  const raw = typeof e.code === "string" ? e.code : typeof e.type === "string" ? e.type : "";
  return /^[a-z0-9_]{1,64}$/.test(raw) ? raw : "load_error";
};

/**
 * Stripe.js for one panel, as ONE promise the Elements provider keeps for its whole life (Elements never accepts a
 * different `stripe` later). It only ever resolves with Stripe; a failed load is reported by `load()` returning false,
 * and calling `load()` again re-requests the script (`loadStripe` clears its own cache on failure) and resolves the SAME
 * promise — so a retry never remounts the panel. Nothing is requested until `load()` is called (client effect only).
 */
function createStripeLoad() {
  let resolve!: (stripe: Stripe) => void;
  const promise = new Promise<Stripe>((r) => {
    resolve = r;
  });
  let loaded = false;
  const load = async () => {
    if (loaded) return true;
    const stripe = await loadStripe(publishableKey()).catch(() => null);
    if (stripe && !loaded) {
      loaded = true;
      resolve(stripe);
    }
    return loaded;
  };
  return { promise, load };
}
type StripeLoad = ReturnType<typeof createStripeLoad>;

/** The signed-webhook status of an order — the only thing that may say authorised or paid. */
async function readOrderStatus(documentId: string) {
  const res = await fetch(`/api/orders/${encodeURIComponent(documentId)}/status`, { cache: "no-store" }).catch(() => null);
  return res?.ok ? ((await res.json().catch(() => null)) as OrderStatusRead | null) : null;
}

type Answer = (InpageResult & { remainingCart?: ReturnType<typeof readDesignCart> }) | { booked: false; reason: string } | null;

/** The export's slot, re-found whenever the design re-renders (it only exists while the pay page is up). */
function useSlot(): HTMLElement | null {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const find = () => {
      const el = document.querySelector<HTMLElement>(INPAGE_CARD_SLOT);
      setSlot((prev) => (prev === el ? prev : el));
    };
    find();
    const mo = new MutationObserver(find);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);
  return slot;
}

/**
 * Whether the slot is actually displayed: connected, not inside `display: none` (CARD DETAILS and the slot itself are CSS
 * display toggles in the design — `payCardDisplay`, `payInpageCardDisplay`), and wider than zero. The slot exists while
 * My Cart is on screen, so finding it is not enough: Stripe's Payment Element mounted into a hidden, zero-width slot
 * created no card input (staging replay 2026-10-20). Re-checked on the slot's or any ancestor's style/class/hidden change
 * and on the slot's own size change.
 */
function useSlotDisplayed(slot: HTMLElement | null): boolean {
  const [displayed, setDisplayed] = useState(false);
  useEffect(() => {
    const check = () => setDisplayed(!!slot && slot.isConnected && slot.getBoundingClientRect().width > 0);
    check();
    if (!slot) return;
    const mo = new MutationObserver(check);
    for (let el: HTMLElement | null = slot; el; el = el.parentElement) {
      mo.observe(el, { attributes: true, attributeFilter: ["style", "class", "hidden"] });
    }
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(check);
    ro?.observe(slot);
    return () => {
      mo.disconnect();
      ro?.disconnect();
    };
  }, [slot]);
  return displayed;
}

/** Answers the design's `sg:pay` (same-origin only, reply pinned to this origin). `null` = no reply. */
function usePayAnswer(answer: (order: PayOrder) => Promise<Answer>) {
  const ref = useRef(answer);
  useEffect(() => {
    ref.current = answer;
  });
  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const d = ev.data as { type?: string; order?: PayOrder } | null;
      if (d?.type !== "sg:pay") return;
      void ref.current(d.order ?? {}).then((result) => {
        if (result) window.postMessage({ type: "sg:payresult", result }, window.location.origin);
      });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
}

// ── the Kit's "Payment details" block, verbatim tokens (sg-beta-v2.01.html `data-sg-payment-details`) ──
const S = {
  panel: { display: "flex", flexDirection: "column", gap: 12, marginTop: 20, padding: 18, background: "#fff", border: "1px solid var(--sg-border)", borderRadius: 12 },
  h2: { margin: 0, fontFamily: "var(--sg-font-secondary)", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" },
  lead: { margin: "5px 0 0", fontSize: 12.5, color: "var(--sg-grey-600)" },
  row: { display: "flex", flexWrap: "wrap", gap: 12 },
  label: { display: "block", marginBottom: 6, fontFamily: "var(--sg-font-secondary)", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--sg-grey-500)" },
  input: { width: "100%", height: 46, padding: "0 13px", background: "#fff", border: "1px solid var(--sg-border-strong)", borderRadius: 10, fontFamily: "var(--sg-font-web)", fontSize: 14.5, color: "var(--sg-ink-900)" },
  notice: { display: "flex", alignItems: "center", gap: 12, padding: "13px 16px", background: "var(--sg-orange-50)", border: "1px solid var(--sg-color-avalanche-orange)", borderRadius: 12, fontSize: 13.5, lineHeight: 1.5, color: "var(--sg-ink-900)" },
} satisfies Record<string, React.CSSProperties>;

// Stripe's iframe cannot read CSS variables, so the Kit's values are passed as literals (tokens.css).
const APPEARANCE = {
  theme: "stripe" as const,
  variables: {
    colorPrimary: "#FC500E",
    colorText: "#111417",
    colorTextSecondary: "#7E8A92",
    colorDanger: "#C4271B",
    borderRadius: "10px",
    fontFamily: "Roboto, -apple-system, 'Segoe UI', sans-serif",
    fontSizeBase: "14.5px",
  },
  rules: {
    ".Input": { border: "1px solid #CDD5DA", boxShadow: "none", padding: "13px" },
    ".Input:focus": { border: "1px solid #FC500E", boxShadow: "none" },
    ".Label": { fontSize: "10px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7E8A92" },
  },
};

function PanelHead({ payAtShop = false }: { payAtShop?: boolean }) {
  return (
    <div>
      <h2 style={S.h2}>Payment details</h2>
      {payAtShop ? (
        <p style={S.lead} data-inpage-card-pay-at-shop-lead>
          No card needed. Request a reservation, then pay at the shop on arrival if they accept.
        </p>
      ) : (
        <p style={S.lead}>Stripe securely handles your card details.</p>
      )}
    </div>
  );
}

/**
 * The BACKEND's price for exactly the displayed cart (`customer-cart-quote`), fetched before any card is
 * taken and re-fetched when the cart changes. The browser derives no figure of its own; a failed quote is
 * shown as a failure and leaves `pay` refusing — never a stand-in number.
 */
function useQuote(mountainCode: string, hostItems: CartItem[]): { state: QuoteState; retry: () => void } {
  const [state, setState] = useState<QuoteState>({ status: "loading" });
  const [tick, setTick] = useState(0);
  const items = useRef(hostItems);
  // synced after commit, not during render; declared before the effect below so it runs first on every commit
  useEffect(() => {
    items.current = hostItems;
  });
  useEffect(
    () => startDesignQuoteWatch({ mountainCode, getHostItems: () => items.current, onState: setState }),
    [mountainCode, tick],
  );
  return { state, retry: () => setTick((n) => n + 1) };
}

function CardPanel({
  slot,
  stripeLoad,
  mountainCode,
  hostItems,
  initialAmountCents,
}: {
  slot: HTMLElement | null;
  stripeLoad: StripeLoad;
  mountainCode: string;
  hostItems: CartItem[];
  initialAmountCents: number;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [country, setCountry] = useState("");
  const [postal, setPostal] = useState("");
  /** W17 — fields whose error is on screen (revealed on blur / a refused Pay, dropped once valid) and Stripe's own card message */
  const [revealed, setRevealed] = useState<string[]>([]);
  const [cardError, setCardError] = useState<string | null>(null);
  const [summaryOn, setSummaryOn] = useState(false);
  /** W17 — mirrors `locked` for render (refs are not read in render): a do-not-pay-again lock is being held */
  const [recovering, setRecovering] = useState(false);
  /** set by `pay` when the refusal is a field gap: the fields say so, not a payment notice */
  const fieldRefusal = useRef(false);
  /** whether Stripe's card entry has appeared; `loadAttempt` keys the Payment Element, so Try again mounts a fresh one */
  const [cardLoad, setCardLoad] = useState<CardLoad>({ kind: "loading" });
  const [loadAttempt, setLoadAttempt] = useState(0);
  const ready = cardLoad.kind === "ready";
  /** CARD DETAILS is on screen. The Payment Element is first mounted only then and stays mounted after, so a typed card,
   *  3-D Secure and this panel's billing / order / lock state all survive the design hiding and showing the step. */
  const displayed = useSlotDisplayed(slot);
  const [cardMounted, setCardMounted] = useState(false);
  const cardLoadNow = useRef(cardLoad);
  cardLoadNow.current = cardLoad;
  /** the one silent retry this panel gets before a load failure is shown */
  const autoRetried = useRef(false);
  const loadAlertRef = useRef<HTMLDivElement>(null);
  /** the order this visit created (a retry after a declined card pays the SAME order) + the element's size */
  const state = useRef<InpagePaymentState>({ order: null, elementAmountCents: initialAmountCents });
  /** set once the card has been handed off for good: further presses must not create a second order */
  const locked = useRef<string | null>(null);
  /** sets or lifts the do-not-pay-again lock; the design is told (`recover`) so its Pay still reaches this panel's lock answer */
  const lock = (reason: string | null) => {
    locked.current = reason;
    setRecovering(!!reason);
  };
  const busy = useRef(false);
  const { state: quote, retry: retryQuote } = useQuote(mountainCode, hostItems);
  /** M4 · the server's reservation as last read (render mirror) and as held by the lock (refs are not read in render) */
  const [reservation, setReservation] = useState<ReservationFacts | null>(null);
  const reservedLock = useRef<ReservationFacts | null>(null);
  /** M4 · ONLY the server's typed zero-due plan on the current quote (or a reservation this visit already holds) drops the card */
  const payAtShop = (quote.status === "ok" && !!quote.quote.payment) || !!reservation;
  // The card entry is mounted once the server has said how this booking is paid (never for a pay-at-shop quote) and CARD DETAILS
  // is displayed; it then stays mounted, so a typed card survives the design hiding and showing the step.
  if (displayed && !cardMounted && !payAtShop && quote.status !== "loading") setCardMounted(true);

  // RENTAL GUESTS (Ski Butlers Whistler): asked only when the server's quote named rented lines. Page memory only - never storage, a
  // URL, an event or a log. The signed-in account's own name/email/phone prefill the first guest; no measurement is ever invented.
  const rentals = quote.status === "ok" ? quote.quote.rentals : undefined;
  const session = useCartStore((s) => s.session);
  const contact = { name: [session.firstName, session.lastName].filter(Boolean).join(" "), email: session.email, phone: session.phone };
  const [people, setPeople] = useState<RentalPerson[]>(() => [blankRentalPerson("g1", contact)]);
  const [assignments, setAssignments] = useState<RentalAssignments>({});
  /** set once an order request carrying these guests may have reached the server: the attempt key would replay THAT order's guests */
  const [guestLock, setGuestLock] = useState(false);
  /** the server said this attempt already holds an order with OTHER rental details (409 rental_replay_conflict) */
  const replayConflict = useRef<string | null>(null);
  useEffect(() => {
    // a profile that loads after mount fills only the first guest's still-blank contact fields
    setPeople((ps) => ps.map((p, i) => (i === 0 ? { ...p, name: p.name || contact.name, email: p.email || (contact.email ?? ""), phone: p.phone || (contact.phone ?? "") } : p)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.name, contact.email, contact.phone]);
  const rentalLineIds = Object.keys(rentals ?? {});
  /** the design's own display name per cart line, for the guest section's item headings (names only, nothing priced) */
  const rentalLineNames = (): Record<string, string> => {
    const names: Record<string, string> = {};
    for (const l of readDesignCart(window.localStorage)) if (l.name) names[l.id] = l.name;
    return names;
  };
  const guestErrors: Record<string, string> = {};
  const guestLabels: Record<string, string> = {};
  if (rentals) {
    // only people some item uses are checked (and sent): an unused guest, the prefilled first one included, never blocks Pay
    for (const { person: p, index: n, errors } of usedRentalPeopleErrors(assignments, people)) {
      for (const [f, m] of Object.entries(errors) as [RentalGuestField, string][]) {
        guestErrors[rentalFieldId(p.id, f)] = m;
        guestLabels[rentalFieldId(p.id, f)] = `Guest ${n + 1} · ${RENTAL_FIELD_LABEL[f]}`;
      }
    }
    for (const [k, m] of Object.entries(rentalAssignmentErrors(rentals, assignments, people))) {
      const cut = k.lastIndexOf("#");
      const id = rentalUnitId(rentalLineIds.indexOf(k.slice(0, cut)), Number(k.slice(cut + 1)));
      guestErrors[id] = m;
      guestLabels[id] = "Who uses this item";
    }
  }
  // FOUNDER CHECKOUT REQUEST 2026-10-27 · Stripe's presentment currency is the SERVER quote's currency, never a hard-coded one and
  // never the header's display choice on its own: Elements starts in CAD (the source currency) and follows the quote it is paying.
  // Which wallets and methods Stripe offers (Apple Pay, Google Pay, Link) depends on that currency, so eligibility follows it too.
  const quoteCurrency = quote.status === "ok" && /^[A-Za-z]{3}$/.test(quote.quote.currency) ? quote.quote.currency.toLowerCase() : null;
  useEffect(() => {
    // Elements presents the server quote's currency (never converted here); a failed update surfaces at submit/confirm, which refuse
    if (elements && quoteCurrency) void elements.update({ currency: quoteCurrency });
  }, [elements, quoteCurrency]);
  // SAVED CARDS (founder checkout request 2026-10-27 · B3 /api/me/cards): a signed-in member may choose one of their OWN saved cards.
  // The list is the backend's owner-bound customer; the PaymentIntent is created on that same customer, so Stripe itself refuses a
  // card that is not this member's. Choosing one is the member's explicit act; "Use a new card" is the default. Nothing is saved here.
  const signedIn = isSignedIn(session);
  // PR #458 review F2 · the list, and the member's explicit choice, belong to ONE verified session identity (the bearer, or the
  // server-verified cookie session's identity generation — the same identity the quote path keys on), not to "someone is signed in".
  // Both are stored WITH the owner they were read for and only used while that owner is still the session: a change of member
  // (A → B, even with no signed-out moment between) hides A's cards and choice in the very next render, asks for B's list, and a late
  // answer for A is dropped. The identity is compared in memory only; it is never sent, stored or shown.
  const owner = useCartStore((st) => sessionIdentityOf(st));
  const ownerNow = () => sessionIdentityOf(useCartStore.getState());
  const [savedRead, setSavedRead] = useState<OwnedSavedCards | null>(null);
  const [savedChoice, setSavedChoice] = useState<OwnedSavedChoice | null>(null);
  const savedCards = savedCardsFor(owner, savedRead);
  const savedCardId = savedChoiceFor(owner, savedRead, savedChoice);
  const setSavedCardId = (id: string | null) => setSavedChoice(id && owner ? { owner, id } : null);
  useEffect(() => {
    // a new owner (or none) starts from nothing: the previous member's list and explicit choice are cleared at once
    setSavedRead(null);
    setSavedChoice(null);
    if (!owner) return;
    // an answer for a member who is no longer the session (or for an effect already replaced) is never shown
    return loadOwnedSavedCards({ owner, ownerNow, fetchImpl: (...a) => fetch(...a), onRead: setSavedRead });
  }, [owner]);
  /** the order this visit holds was created at THIS quote currency: a later currency choice never pays it */
  const heldCurrency = useRef<string | null>(null);
  const latest = useRef({ stripe, elements, country, postal, hostItems, quote, people, assignments, guestErrors, savedRead, savedChoice });
  latest.current = { stripe, elements, country, postal, hostItems, quote, people, assignments, guestErrors, savedRead, savedChoice };

  /** true from the accepted `sg:pay` until the answer: the panel shows a prominent processing status meanwhile */
  const [processing, setProcessing] = useState(false);
  /** the last answer, said truthfully: `retry` = nothing was charged and Pay may be pressed again; `uncertain` = do not pay again */
  const [outcome, setOutcome] = useState<{ kind: "retry" | "uncertain" | "reserved"; text: string; checked?: boolean } | null>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const outcomeRef = useRef<HTMLDivElement>(null);
  const [checking, setChecking] = useState(false);
  /** the order a redirect return is about — checked, never paid through (Pay still goes through createDesignOrder's attempt key) */
  const resumedOrder = useRef<{ documentId: string; clientTotal: number | null } | null>(null);
  /** this page's copy of the per-order records (saved to sessionStorage too, for a redirect return or a reload) */
  const snapshots = useRef(new Map<string, OrderCartSnapshot>());
  const snapshotFor = (documentId: string) =>
    snapshots.current.get(documentId) ?? readOrderSnapshot(window.sessionStorage, documentId);

  /**
   * W17 — a recovered order (redirect return, status check) has settled: the carts lose only what THAT order covered, from
   * the record saved when it was created. No record → ownership unknown → both carts are left as they are. The record is
   * then marked settled, so a repeated check or a reload of the same return address changes nothing more. Returns the
   * design lines to keep.
   */
  function settleRecoveredCarts(documentId: string) {
    const cart = useCartStore.getState();
    const snapshot = snapshotFor(documentId);
    const plan = cartsAfterSettledOrder(snapshot, {
      lines: readDesignCart(window.localStorage),
      host: cart.items.map((i) => ({ id: i.id, qty: i.qty })),
    });
    for (const id of plan.hostRemove) cart.removeItem(id);
    for (const [id, qty] of plan.hostQty) cart.setQty(id, qty);
    markSettled(snapshot);
    return plan.remainingCart;
  }
  function markSettled(snapshot: OrderCartSnapshot | null) {
    if (snapshot && !snapshot.settled) snapshots.current.set(snapshot.documentId, markOrderSnapshotSettled(window.sessionStorage, snapshot));
  }

  // M4 · a pay-at-shop booking takes no card, so it has no billing gap: nothing is published to the design's summary or shown
  const errors: Record<FieldKey, string> = {
    country: country || payAtShop ? "" : "Select the country of your card's billing address.",
    postal: postal.trim() || payAtShop ? "" : "Enter the postal code of your card's billing address.",
    card: payAtShop ? "" : (cardError ?? ""),
  };
  const shown = visibleErrors(errors, revealed);
  const shownGuest = visibleErrors(guestErrors, revealed);
  const guestsLocked = guestLock || recovering || processing;
  const focusField = (ids: readonly string[]) =>
    focusFirstError(ids, { document, matchMedia: (q) => window.matchMedia(q) }, { focusers: { [FIELD.card]: () => elements?.getElement("payment")?.focus() } });
  // W17 — CARD DETAILS has ONE top summary (the design's). The billing errors join it: published here as messages only (never
  // the values) — always, including for an order this visit already holds (a declined, still-payable order is no proof of a
  // billing address). A Pay the design refused for any gap (`sg:payinvalid`) reveals these fields' own messages too, and retires
  // this panel's card-only summary so two never show at once. `recover` is set only while a do-not-pay-again lock is held: then
  // the design lets Pay reach this panel, whose lock answer and read-only "Check payment status" come before any field check.
  useEffect(() => {
    const billing = { country: errors.country, postal: errors.postal, recover: recovering };
    window.__SG_PAY_BILLING = billing;
    window.dispatchEvent(new CustomEvent("sg:paybilling", { detail: billing }));
  }, [errors.country, errors.postal, recovering]);
  useEffect(
    () => () => {
      delete window.__SG_PAY_BILLING;
      window.dispatchEvent(new CustomEvent("sg:paybilling", { detail: null }));
    },
    [],
  );
  useEffect(() => {
    const onInvalid = (ev: Event) => {
      const keys = (ev as CustomEvent<{ keys?: unknown }>).detail?.keys;
      const gaps = (["country", "postal"] as const).filter((k) => Array.isArray(keys) && keys.includes(k));
      setSummaryOn(false);
      if (gaps.length) setRevealed((r) => revealOnSubmit(r, gaps));
    };
    window.addEventListener("sg:payinvalid", onInvalid);
    return () => window.removeEventListener("sg:payinvalid", onInvalid);
  }, []);
  const fieldProps = (key: "country" | "postal") => ({
    id: FIELD[key],
    "aria-invalid": shown[key] ? true : undefined,
    "aria-describedby": shown[key] ? fieldMessageId(FIELD[key]) : undefined,
    onBlur: () => setRevealed((r) => revealOnBlur(r, key)),
  });

  usePayAnswer(async (order) => {
    if (busy.current) return null; // the design is already showing this submission as in flight
    if (locked.current) {
      setOutcome({ kind: reservedLock.current ? "reserved" : "uncertain", text: locked.current });
      return { booked: false, reason: locked.current };
    }
    busy.current = true; // synchronous, before any await: a repeated message cannot start a second attempt
    setOutcome(null);
    setProcessing(true);
    fieldRefusal.current = false;
    let settled = false;
    try {
      const result = await pay(order);
      if ("authorized" in result) {
        settled = true; // the design takes over (the slot goes away); no flicker back to the entry state
      } else if ("retry" in result && result.reserved) {
        setOutcome({ kind: "reserved", text: result.reason });
      } else if (!fieldRefusal.current) {
        const uncertain = "retry" in result && !result.retry;
        setOutcome({ kind: uncertain ? "uncertain" : "retry", text: result.reason });
      }
      return result;
    } catch {
      // Something threw after the attempt was dispatched: whether the card was charged is NOT known. No retry.
      const reason =
        "Something went wrong while processing your payment, and we can't tell whether it went through. Do not pay again — check My Trips or your email before trying anything else.";
      lock(reason);
      setOutcome({ kind: "uncertain", text: reason });
      return { booked: false, retry: false, reason } satisfies InpageResult;
    } finally {
      busy.current = false;
      if (!settled) setProcessing(false);
    }
  });

  // Move attention to the status once, when processing starts (not on every render, and Stripe's own 3-D Secure UI is left alone).
  useEffect(() => {
    if (!processing) return;
    const el = statusRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [processing]);

  // W17 — a payment notice is focused once when it appears (it is also an alert), so keyboard and screen-reader users land on it.
  useEffect(() => {
    if (!outcome) return;
    const el = outcomeRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView?.({ block: "center", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }, [outcome]);

  // Stripe.js is requested once the server has said this booking takes a card (never for a pay-at-shop quote); whether card entry
  // arrived is judged only while it is displayed (below).
  const quoteKnown = quote.status !== "loading";
  useEffect(() => {
    if (!payAtShop && quoteKnown) void stripeLoad.load();
  }, [stripeLoad, payAtShop, quoteKnown]);

  // Card entry that does not appear is said, never left blank: Stripe.js not arriving, or no `ready` within the timeout.
  // The clock runs only while CARD DETAILS is displayed and the element is mounted; hiding the step stops it, showing it
  // again starts a full wait. A `ready` after the timeout still wins; Stripe's own `loaderror` is final until Try again.
  useEffect(() => {
    if (!displayed || !cardMounted || payAtShop) return;
    let alive = true;
    void stripeLoad.load().then((ok) => {
      if (alive && !ok && cardLoadNow.current.kind === "loading") cardLoadFailed("stripe_js_unavailable");
    });
    const timer = setTimeout(() => {
      if (alive && cardLoadNow.current.kind === "loading") cardLoadFailed("timeout");
    }, CARD_LOAD_TIMEOUT_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [stripeLoad, loadAttempt, displayed, cardMounted, payAtShop]);

  /** A load failure: retried once silently, then shown. Logged with Stripe's code (never its message) either way. */
  function cardLoadFailed(reason: string) {
    const silent = !autoRetried.current && !busy.current;
    console.warn(`[sg-card] Stripe card entry did not load (${reason})${silent ? "; mounting it once more" : ""}`);
    if (silent) {
      autoRetried.current = true;
      setLoadAttempt((n) => n + 1);
      return;
    }
    setCardLoad({ kind: "failed", reason });
  }

  /** Try again: a fresh Payment Element (and Stripe.js again if it never arrived). No order, intent or confirm; nothing else in the panel changes. */
  function retryCardLoad() {
    if (busy.current) return;
    setCardLoad({ kind: "loading" });
    setLoadAttempt((n) => n + 1);
  }

  /**
   * W17 — "Check payment status": an uncertain outcome is reconciled from the server's signed-webhook status before anything
   * else may happen. It never creates an order, confirms a card or replays a payment. Only a server `payment_failed` lifts
   * the do-not-pay-again lock; authorised/paid go to the design exactly as a finished payment would.
   */
  /** M4 · a reservation (or an unconfirmed M4 order) is held: a second press replays nothing new and is answered with this */
  function showLocked(r: ReasonResult, checked = false) {
    const facts = r.reserved ?? null;
    reservedLock.current = facts;
    if (facts) {
      setReservation(facts);
      if (state.current.order) state.current.order.reserved = facts;
    }
    lock(r.reason);
    setOutcome({ kind: facts ? "reserved" : "uncertain", text: r.reason, ...(checked ? { checked: true } : {}) });
  }

  /**
   * M4 · "Start a new checkout", offered ONLY on a definite decline. One fresh read must still say declined; then the old order's
   * record is settled (its lines stay in the cart — nothing was bought), ONLY the attempt that created it is ended and the
   * reservation lock is lifted, so the next Request is a NEW order for the basket as it is now. Anything else keeps the lock.
   */
  async function startFreshCheckout() {
    const target = state.current.order ?? resumedOrder.current;
    const facts = reservedLock.current;
    if (!target || facts?.status !== "declined" || checking || busy.current) return;
    setChecking(true);
    try {
      const r = await startFreshAfterDecline({
        orderId: target.documentId,
        plan: facts.plan,
        snapshot: snapshotFor(target.documentId),
        deps: { readStatus: readOrderStatus, endAttemptIf: endCheckoutAttemptIf, markSettled: (snap) => markSettled(snap) },
      });
      if (!r.fresh) {
        showLocked(r.result as ReasonResult, true);
        return;
      }
      state.current.order = null;
      resumedOrder.current = null;
      reservedLock.current = null;
      setReservation(null);
      replayConflict.current = null;
      setGuestLock(false);
      lock(null);
      setOutcome(null);
    } catch {
      showLocked(RESERVATION_UNCONFIRMED as ReasonResult, true);
    } finally {
      setChecking(false);
    }
  }

  async function checkStatus(target = state.current.order ?? resumedOrder.current) {
    if (!target || checking || busy.current) return;
    const snapshot = snapshotFor(target.documentId);
    // M4 · an order created under the server's pay-at-shop plan is judged ONLY as a reservation: never authorised, never paid
    const plan = snapshot?.payment ?? (state.current.order?.documentId === target.documentId ? state.current.order.reserved?.plan : undefined);
    if (plan) {
      setChecking(true);
      try {
        const r = await checkReservation({ orderId: target.documentId, plan, deps: { readStatus: readOrderStatus } });
        showLocked("waiting" in r ? (RESERVATION_UNCONFIRMED as ReasonResult) : (r as ReasonResult), true);
      } catch {
        showLocked(RESERVATION_UNCONFIRMED as ReasonResult, true);
      } finally {
        setChecking(false);
      }
      return;
    }
    setChecking(true);
    try {
      const r = await checkPaymentStatus({
        orderId: target.documentId,
        // the order's own count: the server's lines (preferred by the judge), else its record — never today's basket
        lineCount: snapshot ? snapshot.lines.reduce((n, l) => n + (Number(l.qty) || 0), 0) : 0,
        clientTotal: target.clientTotal,
        // only the attempt that created THIS order ends; an unknown owner ends nothing
        deps: { readStatus: readOrderStatus, endAttempt: () => endCheckoutAttemptIf(snapshot?.attemptId ?? null), emitPurchase: emitPurchaseOnce },
      });
      if ("waiting" in r) {
        setOutcome({ kind: "uncertain", text: STILL_WAITING, checked: true });
      } else if ("authorized" in r) {
        lock(PLACED);
        setOutcome(null);
        const remainingCart = settleRecoveredCarts(target.documentId);
        window.postMessage({ type: "sg:payresult", result: { ...r, remainingCart } }, window.location.origin);
      } else if (r.retry) {
        lock(null); // the server says this attempt failed: nothing is held, Pay may be pressed again
        setOutcome({ kind: "retry", text: r.reason });
      } else {
        lock(r.reason);
        setOutcome({ kind: "uncertain", text: r.reason, checked: true });
      }
    } catch {
      setOutcome({ kind: "uncertain", text: STILL_WAITING, checked: true });
    } finally {
      setChecking(false);
    }
  }

  // FOUNDER RULING 2026-09-26 — back from a redirect-method payment (Stripe returned to this page with
  // `?sg_order=`). Nothing is asked of the customer: the server's own status is read and the design is
  // told exactly what a card payment would have told it. The URL parameter is never an outcome.
  const resumed = useRef(false);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const orderId = q.get("sg_order");
    if (!orderId || resumed.current) return;
    resumed.current = true;
    // W17 — the order is judged by the record saved when it was created (this tab), never by today's basket; without one
    // its total is not printed, its count comes from the server's own lines, and no cart or attempt is touched.
    const snapshot = snapshotFor(orderId);
    resumedOrder.current = { documentId: orderId, clientTotal: snapshot?.clientTotal ?? null };
    if (snapshot?.payment) {
      // M4 · a pay-at-shop order never went through Stripe: whatever the URL says, it is judged only as a reservation
      void checkReservation({ orderId, plan: snapshot.payment, deps: { readStatus: readOrderStatus } }).then((r) => {
        const shown = ("waiting" in r ? RESERVATION_UNCONFIRMED : r) as ReasonResult;
        showLocked(shown);
        window.postMessage({ type: "sg:payresult", result: shown }, window.location.origin);
      });
      return;
    }
    void resumeAfterRedirect({
      orderId,
      redirectStatus: q.get("redirect_status"),
      lineCount: snapshot ? snapshot.lines.reduce((n, l) => n + (Number(l.qty) || 0), 0) : 0,
      displayedTotal: snapshot?.clientTotal ?? Number.NaN,
      deps: {
        readStatus: readOrderStatus,
        sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
        endAttempt: () => endCheckoutAttemptIf(snapshot?.attemptId ?? null),
        emitPurchase: emitPurchaseOnce,
      },
    }).then((result) => {
      if ("authorized" in result) {
        lock(PLACED);
        window.postMessage({ type: "sg:payresult", result: { ...result, remainingCart: settleRecoveredCarts(orderId) } }, window.location.origin);
        return;
      }
      // W17 — in-page, this panel (not the design's banner) shows the outcome, once
      if (!result.retry) lock(result.reason);
      setOutcome({ kind: result.retry ? "retry" : "uncertain", text: result.reason });
      window.postMessage({ type: "sg:payresult", result }, window.location.origin);
    });
  }, [initialAmountCents]);

  // W17 — a reload in the same tab, during the SAME checkout attempt that already created an order (its record is in this tab):
  // that order's status is read and reconciled through the read-only "Check payment status" path on arrival, before any field
  // is asked for. Paid / with the operator / in flight: it is held (do not pay again) until the server's verdict — authorised
  // goes to the design as a finished payment. Still payable, failed, closed or unreadable: nothing happens here; Pay (only with
  // valid fields) replays the attempt as before. Never an order, intent or confirm from this path. A redirect return is above.
  const recordChecked = useRef(false);
  useEffect(() => {
    if (recordChecked.current || new URLSearchParams(window.location.search).get("sg_order")) return;
    recordChecked.current = true;
    const snapshot = findAttemptSnapshot(window.sessionStorage, peekCheckoutAttempt());
    if (!snapshot) return;
    if (snapshot.payment) {
      // M4 · a recorded pay-at-shop order is reconciled read-only as a reservation; unreadable = nothing here (Pay replays the SAME order)
      const plan = snapshot.payment;
      void checkReservation({ orderId: snapshot.documentId, plan, deps: { readStatus: readOrderStatus } }).then((r) => {
        if ("waiting" in r) return;
        resumedOrder.current = { documentId: snapshot.documentId, clientTotal: snapshot.clientTotal };
        showLocked(r as ReasonResult);
      });
      return;
    }
    void (async () => {
      const read = await readOrderStatus(snapshot.documentId);
      const phase = read ? orderPhase(read.orderStatus) : null;
      if (phase !== "paid" && phase !== "awaiting_acceptance" && phase !== "in_flight") return;
      const target = { documentId: snapshot.documentId, clientTotal: snapshot.clientTotal };
      resumedOrder.current = target;
      lock(STILL_WAITING); // held until the server's verdict below: never a second order for it
      await checkStatus(target);
    })();
  }, []);

  /** Stripe's Payment Element refused the card details: its own inline message stays, and the summary links to it. */
  function cardRefused(message: string | undefined): string {
    const text = safeUserMessage(message, "Check your card details.");
    fieldRefusal.current = true;
    setCardError(text);
    setRevealed((r) => revealOnSubmit(r, ["card"]));
    setSummaryOn(true);
    requestAnimationFrame(() => focusField([FIELD.card]));
    return text;
  }

  /**
   * The ONE order step both paths share (`createDesignOrder`, same attempt key), with the order's record saved under its id. For an
   * M4 quote the record also keeps the server's pay-at-shop plan, so a reload judges that order only as a reservation.
   */
  function orderStep(order: PayOrder, items: CartItem[], qs: QuoteState, guests: ReturnType<typeof buildRentalGuests>) {
    return async () => {
      const allLines = readDesignCart(window.localStorage);
      const selection = qs.status === "ok" ? qs.quote.checkout : undefined;
      const lines = selection ? allLines.filter((l) => selection.lineIds.includes(l.id)) : allLines;
      const host = items.flatMap((i) => {
        const line = lines.find((l) => l.id === i.id);
        return selection ? (line ? [{ id: i.id, qty: Math.min(i.qty, line.qty) }] : []) : [{ id: i.id, qty: i.qty }];
      });
      setGuestLock(true); // from here the attempt key may carry these guests: changing them could silently keep the earlier ones
      const created = await createDesignOrder({ mountainCode, hostItems: items, order, quote: qs.status === "ok" ? qs.quote : null, guests });
      if (!created.ok && created.refused) setGuestLock(false); // definitely nothing was created: the guests may be corrected
      if (!created.ok && created.existing) replayConflict.current = created.reason; // an order exists: stays locked
      if (created.ok) {
        heldCurrency.current = qs.status === "ok" ? qs.quote.currency : null;
        // W17 — what this order covers, saved under its id (first record wins: a replayed attempt returns the same order)
        const snap = saveOrderSnapshot(window.sessionStorage, {
          documentId: created.documentId,
          attemptId: peekCheckoutAttempt(),
          clientTotal: created.clientTotal,
          lines,
          host,
          ...(selection && qs.status === "ok" ? { checkoutBinding: JSON.stringify([qs.quote.fingerprint, selection.fingerprint]) } : {}),
          ...(created.reserved ? { payment: created.reserved.plan } : {}),
        });
        snapshots.current.set(created.documentId, snap);
      }
      return created;
    };
  }

  /** The rental guests the server's quote asks for, checked before ANY order request (both paths). A refusal reveals the gaps. */
  function rentalGuestsOrRefusal(): { guests: ReturnType<typeof buildRentalGuests> } | { refusal: { booked: false; reason: string } } {
    const { quote: qs, people: guestPeople, assignments: guestPicks, guestErrors: gErr } = latest.current;
    const rentedNow = qs.status === "ok" ? qs.quote.rentals : undefined;
    const guests = rentedNow ? buildRentalGuests(rentedNow, guestPicks, guestPeople) : null;
    if (rentedNow && !guests && !state.current.order) {
      const gaps = Object.keys(gErr).filter((k) => gErr[k]);
      fieldRefusal.current = true;
      setRevealed((r) => revealOnSubmit(r, gaps));
      setSummaryOn(true);
      requestAnimationFrame(() => focusField(gaps));
      return { refusal: { booked: false, reason: `Add the rental guest details to continue. ${NOT_CHARGED}` } };
    }
    return { guests };
  }

  /**
   * M4 · PAY AT THE SHOP. No Stripe, no card, no billing address: the server-priced order is created through the same attempt key and
   * the answer is the server's reservation status, on the reason path. The attempt is NOT ended and no GA4 event is offered; the
   * panel is locked on the reservation, so a further press answers with it and never sends a second order.
   */
  async function reserve(order: PayOrder): Promise<NonNullable<Answer>> {
    const { hostItems: items, quote: qs } = latest.current;
    // an order this visit created on the card path is never turned into a reservation by a later quote
    if (state.current.order && !state.current.order.reserved) return { booked: false, reason: CART_CHANGED_AFTER_CARD };
    const g = rentalGuestsOrRefusal();
    if ("refusal" in g) return g.refusal;
    // the card steps of the shared sequence, answered without a card: each is already settled, so nothing is awaited
    const noCard = () => Promise.resolve(NO_CARD);
    const result = await runInpagePayment({
      address: null,
      lineCount: qs.status === "ok" && qs.quote.checkout ? readDesignCart(window.localStorage).filter((l) => qs.quote.checkout?.lineIds.includes(l.id)).reduce((n, l) => n + l.qty, 0) : lineCount(),
      state: state.current,
      deps: {
        validateCard: noCard,
        createOrder: orderStep(order, items, qs, g.guests),
        requestIntent: () => Promise.resolve({ ok: false as const, reason: `${NO_CARD} ${NOT_CHARGED}` }),
        resizeElement: noCard,
        intentStatus: () => Promise.resolve(null),
        confirm: noCard,
        readStatus: readOrderStatus,
        sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
        endAttempt: () => undefined, // intended no-op: a reservation keeps its attempt, so a press or a reload replays the SAME order
      },
    });
    if (replayConflict.current) {
      const text = replayConflict.current;
      replayConflict.current = null;
      lock(text);
      return { booked: false, retry: false, reason: text } satisfies InpageResult;
    }
    if ("retry" in result && (result.reserved || !result.retry)) showLocked(result);
    return result;
  }

  async function pay(order: PayOrder): Promise<NonNullable<Answer>> {
    // M4 · only the server's typed zero-due plan (or a reservation this visit already holds) takes the pay-at-shop path
    if ((latest.current.quote.status === "ok" && latest.current.quote.quote.payment) || state.current.order?.reserved) return reserve(order);
    const { stripe: st, elements: el, country: c, postal: p, hostItems: items, quote: qs } = latest.current;
    const existingSelection = state.current.order ? snapshotFor(state.current.order.documentId)?.checkoutBinding : undefined;
    if (existingSelection && (qs.status !== "ok" || JSON.stringify([qs.quote.fingerprint, qs.quote.checkout?.fingerprint]) !== existingSelection)) {
      return { booked: false, reason: CART_CHANGED_AFTER_CARD };
    }
    if (cardLoad.kind === "failed") {
      // the panel's own load notice already says this and offers Try again: it is focused instead of a second notice
      fieldRefusal.current = true;
      requestAnimationFrame(() => loadAlertRef.current?.focus());
      return { booked: false, reason: CARD_LOAD_FAILED };
    }
    if (!st || !el || !ready) return { booked: false, reason: `Secure card entry is still loading — try again in a moment. ${NOT_CHARGED}` };
    // No confirmed price → no draft order, no payment (a retried attempt that already holds an order skips this).
    if (!state.current.order && qs.status !== "ok") {
      return { booked: false, reason: qs.status === "failed" ? qs.reason : `Your price is still being confirmed — try again in a moment. ${NOT_CHARGED}` };
    }
    // CURRENCY CHANGE AFTER AN ORDER: the held order is frozen in the currency it was created in; a different currency now on screen
    // never pays it (the shopper sees one currency and would be charged another). Nothing new is created either.
    if (state.current.order && !state.current.order.reserved && heldCurrency.current && qs.status === "ok" && qs.quote.currency !== heldCurrency.current) {
      return { booked: false, reason: `Your order was priced in ${heldCurrency.current}. Switch the currency back to ${heldCurrency.current} to pay it. ${NOT_CHARGED}` };
    }
    // the member's chosen saved card, re-checked against the list just read (a stale choice is never sent)
    // ...and only for the member who is the session at this press (F2): a choice made under another owner is never sent
    const savedPm = savedChoiceFor(ownerNow(), latest.current.savedRead, latest.current.savedChoice);
    const address = { country: c, postal_code: p };
    const billingGap = () => {
      const gaps = (["country", "postal"] as const).filter((k) => (k === "country" ? !c : !p.trim()));
      fieldRefusal.current = true;
      setRevealed((r) => revealOnSubmit(r, gaps));
      requestAnimationFrame(() => focusField(gaps.map((k) => FIELD[k])));
    };
    // W17 — a billing gap is refused HERE, before createOrder / requestIntent / confirm, with or without an order this visit
    // already holds: missing billing never starts a financial request. (The design's one summary normally refuses it first; this
    // is the panel's own guard.) A held lock answered above, and an earlier visit's recorded order is reconciled read-only on
    // mount, so neither waits for these fields.
    // Rental guests are checked here too, before any financial request: the order needs one complete guest per rented unit.
    const g = rentalGuestsOrRefusal();
    if ("refusal" in g) return g.refusal;
    const guests = g.guests;
    if (!billingAddress(address)) {
      billingGap();
      return { booked: false, reason: `Add the billing country and postal code under Payment details. ${NOT_CHARGED}` };
    }

    const acct: Record<string, string> = order.account ?? {};
    const name = [acct.firstName, acct.lastName].filter(Boolean).join(" ").trim();
    const result = await runInpagePayment({
      address,
      lineCount: qs.status === "ok" && qs.quote.checkout ? readDesignCart(window.localStorage).filter((l) => qs.quote.checkout?.lineIds.includes(l.id)).reduce((n, l) => n + l.qty, 0) : lineCount(),
      state: state.current,
      deps: {
        validateCard: async () => {
          if (savedPm) return null; // a saved card has nothing typed to validate; Stripe checks it at confirm
          const r = await el.submit();
          return r.error ? cardRefused(r.error.message) : null;
        },
        createOrder: orderStep(order, items, qs, guests),
        requestIntent: async (documentId, addr) => {
          // the currency of the price shown, sent as an ASSERTION only (the order stays authoritative); a reused order without a shown
          // price asserts nothing
          const shownCurrency = qs.status === "ok" ? qs.quote.currency : null;
          try {
            const res = await fetch(`/api/orders/${encodeURIComponent(documentId)}/payment-intent`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ address: addr, ...(shownCurrency ? { currency: shownCurrency } : {}) }),
            });
            const pi = (await res.json().catch(() => ({}))) as { clientSecret?: unknown; amountCents?: unknown; currency?: unknown; error?: unknown };
            if (!res.ok || typeof pi.clientSecret !== "string") {
              return { ok: false, reason: safeUserMessage(pi.error, START_FAILED) };
            }
            // a payment in another currency than the price shown is never confirmed (Stripe would also refuse Elements in another currency)
            if (shownCurrency && typeof pi.currency === "string" && pi.currency.toUpperCase() !== shownCurrency) {
              return { ok: false, reason: `The payment currency did not match the price shown (${shownCurrency}). ${NOT_CHARGED}` };
            }
            return { ok: true, clientSecret: pi.clientSecret, amountCents: pi.amountCents };
          } catch {
            return { ok: false, reason: OFFLINE };
          }
        },
        resizeElement: async (cents) => {
          if (savedPm) return null; // no Payment Element is confirmed for a saved card: the intent's own amount is what is authorized
          await el.update({ amount: cents });
          const r = await el.submit();
          return r.error ? cardRefused(r.error.message) : null;
        },
        intentStatus: async (clientSecret) => {
          const r = await st.retrievePaymentIntent(clientSecret).catch(() => null);
          return r?.paymentIntent?.status ?? null;
        },
        confirm: async (clientSecret, documentId) => {
          const { error: confirmError } = savedPm
            ? // the member's own saved card on the intent's own customer: Stripe refuses a card that is not attached to it
              await st.confirmPayment({
                clientSecret,
                confirmParams: { payment_method: savedPm, return_url: checkoutReturnUrl(window.location.origin, mountainCode, documentId) },
                redirect: "if_required",
              })
            : await st.confirmPayment({
            elements: el,
            clientSecret,
            confirmParams: {
              return_url: checkoutReturnUrl(window.location.origin, mountainCode, documentId),
              payment_method_data: {
                billing_details: {
                  ...(name ? { name } : {}),
                  ...(acct.email ? { email: acct.email } : {}),
                  ...(acct.phone ? { phone: acct.phone } : {}),
                  address: { country: c.trim().toUpperCase(), postal_code: p.trim() },
                },
              },
            },
            redirect: "if_required",
          });
          if (!confirmError) return null;
          // Stripe's own wording is meant for the customer only for card and validation errors; anything else is said plainly
          const shownToCustomer = confirmError.type === "card_error" || confirmError.type === "validation_error";
          return shownToCustomer ? safeUserMessage(confirmError.message, CARD_NOT_AUTHORIZED) : CARD_NOT_AUTHORIZED;
        },
        readStatus: readOrderStatus,
        sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
        endAttempt: endCheckoutAttempt,
        emitPurchase: emitPurchaseOnce,
        emitPaymentInfo: () => void sendAnalyticsEvent("add_payment_info", { payment_type: "card" }),
      },
    });

    if (replayConflict.current) {
      // an EXISTING booking under this attempt: the do-not-pay-again lock and its status check, never a retry with new details
      const text = replayConflict.current;
      replayConflict.current = null;
      lock(text);
      return { booked: false, retry: false, reason: text } satisfies InpageResult;
    }
    if ("authorized" in result) {
      lock(PLACED);
      const documentId = state.current.order?.documentId;
      if (documentId && snapshotFor(documentId)?.checkoutBinding) {
        // Reuse recovery's quantity-aware cleanup for the exact selected order snapshot.
        // result.orderId can be a display reference; ownership uses the created documentId.
        return { ...result, remainingCart: settleRecoveredCarts(documentId) };
      }
      // The healthy full-cart path keeps its established completion behavior.
      useCartStore.getState().clear();
      if (documentId) markSettled(snapshotFor(documentId));
    } else if ("retry" in result && !result.retry) {
      // handed off, and the server has not left it payable (timed out / closed): never a second order
      lock(result.reason);
    }
    return result;
  }

  if (!slot) return null;
  return createPortal(
    <div data-inpage-card-panel data-inpage-card-ready={ready ? "true" : "false"} data-inpage-card-mode={payAtShop ? "pay_at_shop" : "card"} aria-busy={processing} style={S.panel}>
      <PanelHead payAtShop={payAtShop} />
      {processing ? (
        <div
          ref={statusRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
          data-inpage-card-processing
          style={{ ...S.notice, padding: "20px 18px", outline: "none" }}
        >
          <span
            aria-hidden="true"
            style={{ flex: "none", width: 28, height: 28, borderRadius: "50%", border: "3px solid var(--sg-border-strong)", borderTopColor: "var(--sg-color-avalanche-orange)", animation: "sg-inpage-spin 0.9s linear infinite" }}
          />
          {payAtShop ? (
            <span>
              <strong style={{ display: "block", fontSize: 15 }}>Sending your reservation request…</strong>
              Please keep this page open and don&apos;t press the button again. No card is needed — nothing is charged online.
            </span>
          ) : (
            <span>
              <strong style={{ display: "block", fontSize: 15 }}>Processing your payment…</strong>
              We&apos;re securely checking your card and confirming your order. Please keep this page open and don&apos;t press Pay again. If your bank asks you to verify, complete it in the window that opens.
            </span>
          )}
          <style>{"@keyframes sg-inpage-spin{to{transform:rotate(360deg)}}"}</style>
        </div>
      ) : null}
      {!processing && outcome ? (
        // W17 — one notice, announced once (alert) and focused; dismissing it hides it and nothing else: the typed details,
        // the field errors and a do-not-pay-again lock all stay. The only recovery for an uncertain outcome is a status check.
        <Alert
          ref={outcomeRef}
          tabIndex={-1}
          data-inpage-card-outcome={outcome.kind}
          style={{ ...S.notice, position: "relative", flexDirection: "column", alignItems: "flex-start", gap: 4, padding: "16px 52px 16px 18px", outline: "none", ...(outcome.kind === "retry" ? { borderColor: FIELD_ERROR_COLOR } : {}) }}
        >
          <AlertTitle style={{ fontSize: 14.5, fontWeight: 700 }}>
            {outcome.kind === "reserved"
              ? reservation?.status === "accepted"
                ? "Reservation accepted — pay at the shop"
                : reservation?.status === "declined"
                  ? "Reservation cancelled"
                  : "Reservation requested — not confirmed yet"
              : outcome.kind === "uncertain"
                ? payAtShop ? "Do not book again" : "Do not pay again"
                : payAtShop ? "Your reservation request was not sent" : "Your payment did not go through"}
          </AlertTitle>
          <AlertDescription>{outcome.text}</AlertDescription>
          <AlertActions>
            {(outcome.kind === "uncertain" || (outcome.kind === "reserved" && reservation?.status === "requested")) && (state.current.order ?? resumedOrder.current) ? (
              <button type="button" data-slot="alert-action" data-inpage-card-check disabled={checking} onClick={() => void checkStatus()}>
                {checking ? "Checking…" : outcome.checked ? "Retry status check" : payAtShop ? "Check reservation status" : "Check payment status"}
              </button>
            ) : null}
            {outcome.kind === "reserved" && reservation?.status === "declined" && (state.current.order ?? resumedOrder.current) ? (
              <button type="button" data-slot="alert-action" data-inpage-card-fresh disabled={checking} onClick={() => void startFreshCheckout()}>
                {checking ? "Checking…" : "Start a new checkout"}
              </button>
            ) : null}
            {outcome.kind === "retry" && !payAtShop ? (
              <button type="button" data-slot="alert-action" onClick={() => focusField([FIELD.card])}>
                Review card details
              </button>
            ) : null}
          </AlertActions>
          <AlertDismiss data-inpage-card-dismiss label="Dismiss this message" onClick={() => setOutcome(null)} />
        </Alert>
      ) : null}
      {summaryOn ? (
        <FieldErrorSummary
          data-inpage-card-summary
          items={[
            ...Object.keys(shownGuest).filter((id) => shownGuest[id]).map((id) => ({ id, label: guestLabels[id] ?? "Rental guests", message: shownGuest[id] ?? "" })),
            ...(Object.keys(shown) as FieldKey[]).filter((k) => k === "card").map((k) => ({ id: FIELD[k], label: FIELD_LABEL[k], message: shown[k] ?? "" })),
          ]}
          onSelect={(id) => focusField([id])}
        />
      ) : null}
      <QuoteBlock q={quote} onRetry={retryQuote} />
      {rentals ? (
        <RentalGuests
          rentals={rentals}
          lineNames={rentalLineNames()}
          people={people}
          assignments={assignments}
          shown={shownGuest}
          locked={guestsLocked}
          onPerson={(id, f, v) => {
            if (guestsLocked) return;
            setPeople((ps) => ps.map((x) => (x.id === id ? { ...x, [f]: v } : x)));
            const next = people.find((x) => x.id === id);
            if (next) setRevealed((r) => settleOnChange(r, rentalFieldId(id, f), !rentalPersonErrors({ ...next, [f]: v })[f]));
          }}
          onAssign={(lineId, unit, personId) => {
            if (guestsLocked) return;
            setAssignments((a) => {
              const picks = [...(a[lineId] ?? [])];
              picks[unit] = personId;
              return { ...a, [lineId]: picks };
            });
            setRevealed((r) => settleOnChange(r, rentalUnitId(rentalLineIds.indexOf(lineId), unit), personId !== ""));
          }}
          onAddPerson={() => !guestsLocked && setPeople((ps) => [...ps, blankRentalPerson(`g${ps.length + 1}`)])}
          onBlur={(id) => setRevealed((r) => revealOnBlur(r, id))}
        />
      ) : null}
      {payAtShop ? null : (
      <div style={S.row}>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <label style={{ display: "block" }}>
            <span style={S.label}>Billing country</span>
            <select
              data-inpage-card-country
              required
              value={country}
              {...fieldProps("country")}
              onChange={(e) => {
                setCountry(e.target.value);
                setRevealed((r) => settleOnChange(r, "country", e.target.value !== ""));
              }}
              style={{ ...S.input, border: `1px solid ${shown.country ? FIELD_ERROR_COLOR : "var(--sg-border-strong)"}` }}
            >
              <option value="">Select a country…</option>
              {COUNTRY_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {shown.country ? <FieldError role={undefined} id={fieldMessageId(FIELD.country)}>{shown.country}</FieldError> : null}
        </div>
        <div style={{ flex: "1 1 140px", minWidth: 0 }}>
          <label style={{ display: "block" }}>
            <span style={S.label}>Postal code</span>
            <input
              data-inpage-card-postal
              required
              value={postal}
              {...fieldProps("postal")}
              onChange={(e) => {
                setPostal(e.target.value);
                setRevealed((r) => settleOnChange(r, "postal", e.target.value.trim() !== ""));
              }}
              placeholder="V6B 1A1"
              autoComplete="postal-code"
              style={{ ...S.input, border: `1px solid ${shown.postal ? FIELD_ERROR_COLOR : "var(--sg-border-strong)"}` }}
            />
          </label>
          {shown.postal ? <FieldError role={undefined} id={fieldMessageId(FIELD.postal)}>{shown.postal}</FieldError> : null}
        </div>
      </div>
      )}
      {/* Stripe keeps the card data and shows its own inline card messages; the summary links here through Stripe's focus() */}
      {savedCards.length > 0 && !payAtShop ? (
        <fieldset data-inpage-card-saved style={{ border: 0, margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          <legend style={S.label}>Pay with</legend>
          {savedCards.map((card) => (
            <label key={card.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              <input type="radio" name="sg-saved-card" data-inpage-card-saved-option={card.id} checked={savedCardId === card.id} disabled={processing} onChange={() => setSavedCardId(card.id)} />
              {savedCardLabel(card)}
              {card.isDefault ? <span style={{ opacity: 0.7 }}>(default)</span> : null}
            </label>
          ))}
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
            <input type="radio" name="sg-saved-card" data-inpage-card-saved-option="new" checked={savedCardId === null} disabled={processing} onChange={() => setSavedCardId(null)} />
            Use a new card
          </label>
        </fieldset>
      ) : null}
      {signedIn && !payAtShop ? (
        <p data-inpage-card-manage style={{ ...S.lead, margin: 0, fontSize: 13 }}>
          <a href={accountsUrl("/settings")} target="_blank" rel="noopener">
            Save or manage cards in your account
          </a>
        </p>
      ) : null}
      <div id={FIELD.card} hidden={payAtShop || !!savedCardId}>
        {cardMounted && !payAtShop ? (
          <PaymentElement
            key={loadAttempt}
            onReady={() => setCardLoad({ kind: "ready" })}
            onLoadError={(e) => cardLoadFailed(loadErrorReason(e.error))}
            onChange={(e) => {
              if (!e.complete) return;
              setCardError(null);
              setRevealed((r) => settleOnChange(r, "card", true));
            }}
            options={{ layout: "tabs", wallets: { applePay: "auto", googlePay: "auto" }, fields: { billingDetails: { address: { country: "never", postalCode: "never" } } } }}
          />
        ) : null}
      </div>
      {/* Beside Stripe's iframe, never over or inside it: Stripe sizes and reveals its own frame. */}
      {cardLoad.kind === "loading" && !payAtShop ? (
        <p role="status" data-inpage-card-loading style={{ ...S.lead, margin: 0 }}>
          Loading secure card entry…
        </p>
      ) : null}
      {cardLoad.kind === "failed" && !payAtShop ? (
        <Alert
          ref={loadAlertRef}
          tabIndex={-1}
          data-inpage-card-load-error={cardLoad.reason}
          style={{ ...S.notice, flexDirection: "column", alignItems: "flex-start", gap: 4, padding: "16px 18px", outline: "none" }}
        >
          <AlertTitle style={{ fontSize: 14.5, fontWeight: 700 }}>Secure card entry didn&apos;t load</AlertTitle>
          <AlertDescription>{CARD_LOAD_FAILED}</AlertDescription>
          <AlertActions>
            <button type="button" data-slot="alert-action" data-inpage-card-load-retry disabled={processing} onClick={retryCardLoad}>
              Try again
            </button>
          </AlertActions>
        </Alert>
      ) : null}
    </div>,
    slot,
  );
}

/** No publishable key in this build: say so in the Kit's panel, and answer Pay with the same sentence. */
function Unconfigured({ slot }: { slot: HTMLElement | null }) {
  const reason = `Secure card payment isn't configured on this site yet. ${NOT_CHARGED}`;
  usePayAnswer(() => Promise.resolve({ booked: false, reason }));
  if (!slot) return null;
  return createPortal(
    <div data-inpage-card-panel data-inpage-card-unconfigured style={S.panel}>
      <PanelHead />
      <div style={S.notice}>{reason}</div>
    </div>,
    slot,
  );
}

export default function InpageCardPayment({
  mountainCode,
  hostItems,
  displayedTotal,
}: {
  mountainCode: string;
  hostItems: CartItem[];
  /** what the design's pay summary totals — only sizes the Payment Element; the charge is the order's */
  displayedTotal: number;
}) {
  const slot = useSlot();
  const [stripeLoad] = useState(createStripeLoad);
  // Frozen at mount: the Payment Element is updated explicitly once the server has priced the order.
  const [initialAmountCents] = useState(() => Math.max(50, Math.round((Number.isFinite(displayedTotal) ? displayedTotal : 0) * 100)));
  const options = useMemo(
    () => ({ mode: "payment" as const, amount: initialAmountCents, currency: "cad", captureMethod: "manual" as const, appearance: APPEARANCE }),
    [initialAmountCents],
  );

  if (!publishableKey().startsWith("pk_")) return <Unconfigured slot={slot} />;
  return (
    <Elements stripe={stripeLoad.promise} options={options}>
      <CardPanel slot={slot} stripeLoad={stripeLoad} mountainCode={mountainCode} hostItems={hostItems} initialAmountCents={initialAmountCents} />
    </Elements>
  );
}
